import React from 'react';
import classnames from 'classnames';
import { Button, RadioButton, TextInput, Loader } from 'is24-corecss';
import { useForm } from 'react-hook-form';
import { UserSalutation } from '../../../../../../@types/userAccount';
import styles from './styles';
import type { ProfileBenefitsUserData } from '../../../../../../@types/productBenefits';
import { useGetProductBenefitsPersonalData } from '../../../../../../apiHooks/productBenefits.hook';
import { Msg, useIntl } from '@/i18n';
import { validateSalutation } from '@/utils/validateSalutation.util';

interface Props {
  close: () => void;
  onSubmit: (values: Omit<ProfileBenefitsUserData, 'policyNumber'>) => Promise<void>;
  isLoading: boolean;
}

const SchlüsselnotdienstModifyForm: React.FC<Props> = ({ close, onSubmit, isLoading }) => {
  const { t } = useIntl();
  const { personalData: profile } = useGetProductBenefitsPersonalData();
  const defaultValues = {
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    city: profile?.city || '',
    houseNumber: profile?.houseNumber || '',
    street: profile?.street || '',
    postcode: profile?.postcode || '',
    salutation: profile?.salutation || UserSalutation.FEMALE,
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ defaultValues, values: defaultValues, mode: 'all', criteriaMode: 'all' });

  return (
    <form className={styles.container} onSubmit={handleSubmit(onSubmit)} data-testid='SchlüsselnotdienstModifyForm'>
      <div className={styles.content}>
        <div className={styles.column}>
          {isLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <>
            <label className={styles.radiosLabel}>
              {t({
                id: 'benefits.keyEmergency.modal.form.salutation.label',
                defaultMessage: 'Anrede',
              })}
            </label>
            <div className={styles.radios}>
              <RadioButton
                label={t({
                  id: 'benefits.keyEmergency.modal.form.salutation.options.female',
                  defaultMessage: 'Frau',
                })}
                id={UserSalutation.FEMALE}
                value={UserSalutation.FEMALE}
                isErroneous={!!errors.salutation}
                {...register('salutation', { required: true, validate: validateSalutation })}
                data-testid='Frau'
              />
              <RadioButton
                label={t({
                  id: 'benefits.keyEmergency.modal.form.salutation.options.male',
                  defaultMessage: 'Herr',
                })}
                id={UserSalutation.MALE}
                value={UserSalutation.MALE}
                isErroneous={!!errors.salutation}
                {...register('salutation', { required: true, validate: validateSalutation })}
                data-testid='Herr'
              />
            </div>
            <div className={styles.row}>
              <TextInput.Root id='firstName' invalid={!!errors.firstName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.keyEmergency.modal.form.firstName.label',
                    defaultMessage: 'Vorname',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  placeholder=''
                  {...register('firstName', { required: true })}
                  data-testid='firstName'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='lastName' invalid={!!errors.lastName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.keyEmergency.modal.form.lastName.label',
                    defaultMessage: 'Nachname',
                  })}
                </TextInput.Label>
                <TextInput.Input {...register('lastName', { required: true })} placeholder='' data-testid='lastName' />
              </TextInput.Root>
            </div>
            <div className={classnames(styles.address1, styles.row)}>
              <div className={styles.street}>
                <TextInput.Root id='street' invalid={!!errors.street}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.keyEmergency.modal.form.street.label',
                      defaultMessage: 'Straße',
                    })}
                  </TextInput.Label>
                  <TextInput.Input {...register('street', { required: true })} placeholder='' data-testid='street' />
                </TextInput.Root>
              </div>
              <div className={styles.houseNumber}>
                <TextInput.Root id='houseNumber' invalid={!!errors.houseNumber}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.keyEmergency.modal.form.houseNumber.label',
                      defaultMessage: 'Hausnr.',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    placeholder=''
                    {...register('houseNumber', { required: true })}
                    data-testid='houseNumber'
                  />
                </TextInput.Root>
              </div>
            </div>
            <div className={classnames(styles.row, styles.address2)}>
              <div className={styles.postcode}>
                <TextInput.Root id='postcode' invalid={!!errors.postcode}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.keyEmergency.modal.form.postcode.label',
                      defaultMessage: 'PLZ',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    placeholder=''
                    {...register('postcode', { required: true, pattern: /^[0-9]{5}$/ })}
                    data-testid='postcode'
                  />
                </TextInput.Root>
              </div>
              <div className={styles.city}>
                <TextInput.Root id='city' invalid={!!errors.city}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.keyEmergency.modal.form.city.label',
                      defaultMessage: 'Ort',
                    })}
                  </TextInput.Label>
                  <TextInput.Input placeholder='' {...register('city', { required: true })} data-testid='city' />
                </TextInput.Root>
              </div>
            </div>
          </>
        </div>
      </div>
      <div className={styles.modifyFooter}>
        <Button textStyle={true} onClick={close} type='button' className='margin-right-s'>
          <Msg id='benefits.keyEmergency.modal.form.cancelButtonText' defaultMessage='Abbrechen' />
        </Button>
        <Button type='submit' appearance='secondary' size='small' disabled={isLoading}>
          <Msg id='benefits.keyEmergency.modal.form.saveButtonText' defaultMessage='Speichern' />
        </Button>
      </div>
    </form>
  );
};

export default SchlüsselnotdienstModifyForm;
